import React, { FunctionComponent, useEffect, useState } from 'react'
import styles from './auth.module.scss'
import Title from '@/components/_custom/Title/title'
import UserInput from '@/components/_custom/UserInput/userInput'
import ButtonText from '@/components/_custom/Buttons/ButtonText/ButtonText'
import validator from 'validator'
import { Link, NavigateFunction, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import { SIGN_IN } from '@/modules/API/account/accountHandler'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { useAuth } from '@/modules/context/AuthContext'

interface LoginProps {
    email: string
    setEmail: (email: string) => void
    returnToLogin: () => void
    deviceType?: string
    autoLink?: string
    logo?: string
}

const Login: FunctionComponent<LoginProps> = ({
    email,
    setEmail,
    returnToLogin,
    deviceType,
    autoLink,
    logo
}: LoginProps) => {
    const { t } = useTranslation()

    const { login, pushToken } = useAuth()
    const navigate: NavigateFunction = useNavigate()

    const [emailValid, setEmailValid] = useState<boolean>(validator.isEmail(email) && email !== '')

    const [password, setPassword] = useState<string>('')
    const [passwordValid, setPasswordValid] = useState<boolean>(false)

    const [errorMessage, setErrorMessage] = useState<string>('')

    function handleEmailChange(e: string): void {
        setEmail(e)
    }

    useEffect(() => {
        if (email) {
            localStorage.setItem('email', email)
        } else {
            localStorage.removeItem('email')
        }
    }, [email])

    const [signIn] = useMutation(SIGN_IN, {
        variables: {
            email: email,
            password: password,
            pushToken: pushToken,
            deviceType: deviceType,
            autoLink: autoLink
        },
        onCompleted: ({ signIn }): void => {
            login(signIn.user, signIn.token)

            if (signIn.accountNotCompleted) navigate('/account/profile?account=signup')
            else navigate('/calendar')
        },
        onError: (error): void => {
            setPassword('')
            switch (error.message.split('Message: ')[0]) {
                case 'Wrong password':
                    setErrorMessage(t('passwordIncorrect'))
                    setPasswordValid(false)
                    break
                default:
                    setErrorMessage(t('generalError'))
                    break
            }
        }
    })

    function onKeyPress(e: React.KeyboardEvent<HTMLInputElement>): void {
        if (e.key === 'Enter' && emailValid && passwordValid) {
            signIn()
        }
    }

    return (
        <div className={styles.Form}>
            <div className={styles.Title}>
                <Title text={t('login')} />
            </div>
            <div className={styles.Input}>
                <UserInput
                    id='email'
                    label={t('email')}
                    type={'text'}
                    verify={'email'}
                    unallowedCharacters={'email'}
                    required
                    fullWidth
                    value={email}
                    setValue={handleEmailChange}
                    setValid={setEmailValid}
                    disabled
                />
                <UserInput
                    id='password'
                    label={t('password')}
                    type={'password'}
                    verify={'password'}
                    required
                    fullWidth
                    className={styles.InputField}
                    value={password}
                    setValue={setPassword}
                    setValid={setPasswordValid}
                    onKeyPress={onKeyPress}
                />
            </div>
            {errorMessage && <ErrorMessage error={errorMessage} className={styles.ErrorPosition} />}
            <ButtonText text={t('login')} onClick={signIn} fullWidth disabled={!emailValid || !passwordValid} />
            <Link to='/forgottenPassword' className={styles.Link}>
                {t('forgottenPassword')}
            </Link>
            <a className={styles.Link} onClick={returnToLogin}>
                {t('returnToLogin')}
            </a>
            {logo && <img src={`data:image/png;base64,${logo}`} alt={'logo'} className={styles.Logo} />}
        </div>
    )
}

export default Login
